import { RegionContext } from '@fsd/shared/config/region';
import { useLingui } from '@lingui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useOrganizationHour12 } from './api/use-organization-hour12';
import { useUpdateUserLanguage } from './api/use-update-user-language';
import { useUserLocalePreference } from './api/use-user-locale-preference';
import { useLocaleActivation } from './locale-activation-context';
import { type AppLocale, isAppLocale, writeStoredLocale } from './locale-storage';
import { resolveRegion } from './model/resolve-region';
import { useRegionMappings } from './region-mappings-context';
import { type UserLocale, UserLocaleContext } from './user-locale-context';

/**
 * Lee las preferencias del usuario una vez (va en `AppLayout`, bajo `AuthGuard`: la query necesita
 * sesión) y expone el idioma con `useUserLocale()` y la región con `useRegion()`
 * (`@fsd/shared/config/region`). El `hour12` de la organización se pide salvo que la región del usuario ya
 * lo defina: mientras `GetUserPreferences` carga o si falla, `useFormatters` usa el de la organización,
 * y la región también.
 *
 * El arranque ya activó el último idioma conocido (`FsdContext`); cuando llega la preferencia, manda
 * la del servidor y queda como último conocido para la próxima carga.
 *
 * Activar un idioma puede tardar (el catálogo de `en` es un chunk aparte) o fallar. Cada pedido de
 * idioma (del servidor o del usuario) toma un número; lo que resuelve después de un pedido más nuevo
 * se descarta. Solo se guarda y se manda al servidor un idioma que quedó activo.
 */
export function UserLocaleProvider({ children }: { children: ReactNode }) {
  const activate = useLocaleActivation();
  const { i18n } = useLingui();
  const { userId, serverLocale, region: userRegion } = useUserLocalePreference();
  const mappings = useRegionMappings();
  const organizationHour12 = useOrganizationHour12({
    skip: userRegion?.hour12 != null,
  });
  const updateLanguage = useUpdateUserLanguage();
  const [saveFailed, setSaveFailed] = useState(false);

  /** El idioma activo en Lingui, leído en el momento (no el del último render). */
  const activeLocale = useCallback(
    (): AppLocale => (isAppLocale(i18n.locale) ? i18n.locale : 'es'),
    [i18n],
  );

  // La última elección pedida, aunque su catálogo todavía no cargó: el selector la muestra y las
  // elecciones siguientes se comparan contra ella. `null` = la del idioma activo. El ref la tiene al
  // día dentro del mismo tick; el estado, para el render.
  const requestedRef = useRef<AppLocale | null>(null);
  const [requested, setRequestedState] = useState<AppLocale | null>(null);
  const setRequested = useCallback((next: AppLocale | null) => {
    requestedRef.current = next;
    setRequestedState(next);
  }, []);

  // Número del último pedido de idioma; uno más nuevo invalida lo que resuelva de los anteriores.
  const latestRequest = useRef(0);
  // El usuario ya mandó una elección al servidor en esta montura del layout: desde ahí
  // la preferencia del servidor deja de mandar.
  const sentChoice = useRef(false);

  // Manda el servidor hasta que el usuario elige: después, lo que traiga la caché es el eco de sus
  // propias mutations (que pueden resolver en otro orden) y no debe pisar su última elección.
  useEffect(() => {
    if (!serverLocale || sentChoice.current) return;
    const request = ++latestRequest.current;
    setRequested(serverLocale);
    activate(serverLocale).then(
      () => {
        if (request === latestRequest.current) writeStoredLocale(serverLocale);
      },
      () => {
        // El catálogo no cargó: queda el idioma activo, que el selector vuelve a mostrar.
        if (request === latestRequest.current) setRequested(null);
      },
    );
  }, [activate, serverLocale, setRequested]);

  const setLocale = useCallback(
    async (next: AppLocale) => {
      if (next === (requestedRef.current ?? activeLocale())) return;
      const prev = activeLocale();
      const request = ++latestRequest.current;
      setRequested(next);
      setSaveFailed(false);

      try {
        await activate(next);
      } catch {
        // El catálogo no cargó: no se guarda ni se manda; vuelve al anterior y avisa.
        if (request !== latestRequest.current) return;
        setRequested(prev);
        setSaveFailed(true);
        return;
      }
      // Hay una elección posterior: esta no se guarda ni se manda.
      if (request !== latestRequest.current) return;
      writeStoredLocale(next);

      // Sin usuario todavía (la query no llegó) el cambio queda local; la preferencia del servidor
      // manda cuando llegue.
      if (userId == null) return;
      sentChoice.current = true;
      try {
        await updateLanguage(userId, next);
      } catch {
        // Una respuesta de una elección vieja no revierte la que el usuario hizo después.
        if (request !== latestRequest.current) return;
        setRequested(prev);
        writeStoredLocale(prev);
        setSaveFailed(true);
        // `prev` ya estuvo activo: su catálogo está cargado.
        activate(prev).catch(() => {});
      }
    },
    [activate, activeLocale, setRequested, updateLanguage, userId],
  );

  const locale = requested ?? activeLocale();

  const value = useMemo<UserLocale>(
    () => ({ locale, userId, setLocale, saveFailed }),
    [locale, userId, setLocale, saveFailed],
  );

  // El idioma activo (no el pedido): es el que usa `useFormatters` de fallback. `i18n` es el mismo
  // objeto en cada activación, así que la dependencia es el string.
  const linguiLocale = activeLocale();
  // Sobre los campos, no sobre el objeto de Apollo: una respuesta nueva con los mismos valores no
  // cambia la región ni rerenderiza a sus consumidores.
  const hasUserRegion = userRegion != null;
  const numberLocale = userRegion?.numberLocale;
  const dateLocale = userRegion?.dateLocale;
  const timezone = userRegion?.timezone;
  const userHour12 = userRegion?.hour12;
  const dateMonthFormat = userRegion?.dateMonthFormat;
  const region = useMemo(
    () =>
      resolveRegion({
        activeLocale: linguiLocale,
        userRegion: hasUserRegion
          ? { numberLocale, dateLocale, timezone, hour12: userHour12, dateMonthFormat }
          : null,
        organizationHour12,
        mappings,
      }),
    [
      linguiLocale,
      hasUserRegion,
      numberLocale,
      dateLocale,
      timezone,
      userHour12,
      dateMonthFormat,
      organizationHour12,
      mappings,
    ],
  );

  return (
    <UserLocaleContext.Provider value={value}>
      <RegionContext.Provider value={region}>{children}</RegionContext.Provider>
    </UserLocaleContext.Provider>
  );
}
